import { useEffect, useState } from 'react';
import {
	Box,
	CircularProgress,
	Grid2,
	IconButton,
	SvgIcon,
	Typography,
} from '@mui/material';
import { DownloadIcon } from '../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';
import { isBlobUrl } from '../Utils/Helpers';
import FormattedText from './FormattedText';

interface ChatDataProps {
	position: string;
	text: string;
	chatTime: string;
	fileName?: string;
	fileUrl?: string;
	onDownload?: (url: string) => void;
	caption?: string;
	chatOwner: string;
	isDownloading?: boolean;
	downloadingFile?: string;
	isThisReplyMsg?: boolean;
	repliedMsg?: string;
	isMsgHasReaction?: boolean;
	reaction?: string;
	isFileIsImage?: boolean;
	imageURL?: string;
	isReplyFileImage?: boolean;
}

function MediaPreview({
	src,
	compact = false,
}: {
	src?: string;
	compact?: boolean;
}) {
	const [hasError, setHasError] = useState(!src);

	useEffect(() => {
		setHasError(!src);
	}, [src]);

	if (hasError) {
		return (
			<Box
				sx={{
					width: compact ? 140 : { xs: 180, sm: 240 },
					maxWidth: '100%',
					height: compact ? 92 : 150,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					gap: 0.75,
					backgroundColor: 'rgba(255, 255, 255, 0.52)',
					border: '1px dashed #B9C5CA',
					borderRadius: '5px',
					color: '#667781',
				}}>
				<SvgIcon
					sx={{ fontSize: compact ? 25 : 30 }}
					viewBox="0 0 24 24">
					<path d="M19 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2ZM5 19l3.5-4.5 2.5 3.01L14.5 13l4.5 6H5Zm1.5-9A1.5 1.5 0 1 1 6.5 7a1.5 1.5 0 0 1 0 3Z" />
				</SvgIcon>
				<Typography fontSize="10px" fontWeight={600}>
					Media unavailable
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			component="img"
			src={src}
			alt="Media attachment"
			onError={() => setHasError(true)}
			sx={{
				maxWidth: '100%',
				maxHeight: compact ? 120 : 250,
				width: 'auto',
				height: 'auto',
				borderRadius: '5px',
				display: 'block',
			}}
		/>
	);
}

function ChatBubble(data: ChatDataProps) {
	function getChatBubbleStyle() {
		if (data.position === 'left') {
			return {
				borderTopLeftRadius: '2px',
				borderTopRightRadius: '7px',
				borderBottomLeftRadius: '7px',
				borderBottomRightRadius: '7px',
			};
		} else {
			return {
				borderTopLeftRadius: '7px',
				borderTopRightRadius: '2px',
				borderBottomLeftRadius: '7px',
				borderBottomRightRadius: '7px',
			};
		}
	}

	function getChatOwnerTag(type: string) {
		if (type === 'consultant') {
			return 'Consultant';
		} else {
			return 'AI Assistant';
		}
	}

	return (
		<Grid2
			component={'section'}
			sx={{
				display: 'flex',
				alignItems:
					data.position === 'right' ? 'flex-end' : 'flex-start',
				justifyContent:
					data.position === 'right' ? 'flex-end' : 'flex-start',
				width: '100%',
				minWidth: 0,
			}}>
			<Grid2
				component={'section'}
				sx={{
					position: 'relative',
					maxWidth: { xs: '86%', sm: '78%', lg: '76%' },
					...getChatBubbleStyle(),
					display: 'inline-block',
					minWidth: 0,
					wordBreak: 'break-word',
					backgroundColor:
						data.position === 'left' ? '#FFFFFF' : '#D9FDD3',
					px: 1.25,
					pt: 0.85,
					pb: 0.65,
					boxShadow: '0 1px 1px rgba(11, 20, 26, 0.12)',
				}}>
				{data.position === 'right' && (
					<Typography
						variant="body2"
						textAlign={'left'}
						color={
							data.chatOwner === 'consultant'
								? '#008069'
								: '#54656F'
						}
						fontWeight={'700'}
						fontSize="10px"
						paddingBottom="3px">
						{getChatOwnerTag(data.chatOwner)}
					</Typography>
				)}
				{data.isThisReplyMsg && (
					<Grid2
						sx={{
							backgroundColor:
								data.position === 'left'
									? '#F0F2F5'
									: '#C7EFC0',
							borderRadius: '4px',
							px: 1.5,
							py: 1,
							mb: 1,
							justifyContent: 'center',
							borderLeft: `3px solid ${MAIN_THEME_COLOR}`,
						}}>
						{data.isReplyFileImage ||
						isBlobUrl(data.repliedMsg || '') ? (
							<MediaPreview src={data.repliedMsg} compact />
						) : (
							<FormattedText
								text={data.repliedMsg || ''}
								sx={{ fontSize: '11px', color: '#3B4A54' }}
							/>
						)}
					</Grid2>
				)}
				{data.fileName && data.fileUrl ? (
					<Grid2 container flexDirection={'column'}>
						{data.isFileIsImage && (
							<MediaPreview src={data.imageURL} />
						)}
						<Grid2
							container
							flexDirection={'row'}
							width={'100%'}
							alignItems={'center'}
							sx={{
								backgroundColor:
									data.position === 'left'
										? '#F0F2F5'
										: '#C7EFC0',
								padding: '8px',
								borderRadius: '5px',
								boxShadow: !data.isFileIsImage
									? '0 1px 1px rgba(0, 0, 0, 0.06)'
									: '',
							}}>
							<Typography
								variant="body2"
								style={{
									overflowWrap: 'break-word',
									maxWidth: 'calc(100% - 34px)',
								}}
								color="#111B21">
								{!data.isFileIsImage && data.fileName}
							</Typography>
							{data.isDownloading &&
							data.downloadingFile === data.fileUrl ? (
								<CircularProgress
									sx={{
										color:
											data.position === 'left'
												? '#28696D'
												: MAIN_THEME_COLOR,
										marginLeft: '5px',
										padding: '2px',
									}}
									size={24}
								/>
							) : (
								<IconButton
									sx={{
										marginLeft: '5px',
										padding: '2px',
									}}
									onClick={() =>
										data.onDownload &&
										data.onDownload(
											data.fileUrl ? data.fileUrl : '',
										)
									}>
									<DownloadIcon color={MAIN_THEME_COLOR} />
								</IconButton>
							)}
						</Grid2>
						{data.caption && (
							<FormattedText
								text={data.caption}
								sx={{
									fontSize: '0.75rem',
									color:
										data.position === 'left'
											? '#8E8E8E'
											: '#667781',
									maxWidth: '100%',
									pt: '5px',
									pl: '5px',
								}}
							/>
						)}
					</Grid2>
				) : (
					<FormattedText
						text={data.text}
						sx={{
							fontSize: '13px',
							lineHeight: 1.45,
							color: '#111B21',
						}}
					/>
				)}
				<Typography
					fontSize={'9px'}
					textAlign={data.position === 'left' ? 'start' : 'end'}
					color="#667781"
					paddingTop={'3px'}>
					{data.chatTime}
				</Typography>
				{data.isMsgHasReaction && (
					<Grid2
						sx={{
							position: 'absolute',
							backgroundColor: '#FFFFFF',
							bottom: '-14px',
							[data.position === 'left' ? 'left' : 'right']:
								'20px',
							display: 'flex',
							gap: '10px',
							alignItems: 'center',
							borderRadius: '12px',
							minWidth: '26px',
							height: '24px',
							padding: '2px',
							justifyContent: 'center',
							border: '1px solid #E9EDEF',
							boxShadow: '0 1px 2px rgba(11, 20, 26, 0.12)',
						}}>
						<Typography
							fontSize={'12px'}
							textAlign={'center'}
							color="#111B21">
							{data.reaction}
						</Typography>
					</Grid2>
				)}
			</Grid2>
		</Grid2>
	);
}

function ChatRow(chat: ChatDataProps) {
	return (
		<Grid2
			container
			justifyContent={
				chat.position === 'right' ? 'flex-end' : 'flex-start'
			}
			sx={{ px: { xs: 1.5, sm: 3 }, py: 0.35 }}>
			<ChatBubble
				position={chat.position}
				text={chat.text}
				chatTime={chat.chatTime}
				fileName={chat.fileName}
				fileUrl={chat.fileUrl}
				onDownload={chat.onDownload}
				caption={chat.caption}
				chatOwner={chat.chatOwner}
				isDownloading={chat.isDownloading}
				downloadingFile={chat.downloadingFile}
				isThisReplyMsg={chat.isThisReplyMsg}
				repliedMsg={chat.repliedMsg}
				isMsgHasReaction={chat.isMsgHasReaction}
				reaction={chat.reaction}
				isFileIsImage={chat.isFileIsImage}
				imageURL={chat.imageURL}
				isReplyFileImage={chat.isReplyFileImage}
			/>
		</Grid2>
	);
}

export default ChatRow;
